<template>
  <div
    class="container"
    ref="container"
    :class="{ dimmed: dimmed }"
    :style="containerStyles"
    @click="emitCloseModal"
  >
    <div :class="[className, { 'box-shadow': !dimmed }]" :style="modalStyles">
      <slot />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
export default {
  name: 'Modal',
  props: {
    dimmed: {
      type: Boolean,
      default: false,
    },
    /* Specify the width of the modal in VW. */
    width: {
      type: Number,
      default: 60,
    },
    className: {
      type: String,
      default: 'modal',
    },
    isOrgMessage: {
      type: Boolean,
      default: false,
    },
    additionalContainerStyles: {
      type: Object,
      default: () => ({}),
    },
    additionalModalStyles: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      original: {
        documentY: document.documentElement.scrollTop,
        overflowX: document.body.style.overflowX,
        overflowY: document.body.style.overflowY,
      },
    }
  },
  mounted() {
    document.body.style.overflowX = 'hidden'
    document.body.style.overflowY = 'hidden'
    this.$nextTick(() => {
      this.scrollPosition = 0
      if (this.$refs.container){
        this.$refs.container.firstChild.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  },
  beforeDestroy() {
    document.body.style.overflowX = this.original.overflowX
    document.body.style.overflowY = this.original.overflowY
  },
  methods: {
    emitCloseModal({ target }) {
      if (this.$refs.container === target) {
        this.$emit('close-modal')
      }
    },
  },
  computed: {
    ...mapState(['user']),
    /* Generate modal container styles based on user type. */
    containerStyles() {
      const styles = { top: `0px` }

      if (this.isOrgMessage) {
        styles['width'] = '500px' // $maximum-width
        styles['maxWidth'] = '100vw'

        return styles
      }

      // For employees, container should cover only fixed-width section, but not exceed viewport size
      if (this.user && this.user.isEmployee) {
        styles['width'] = '500px' // $maximum-width
        styles['maxWidth'] = '100vw'

        // All other users see a full screen
      } else {
        styles['width'] = '100vw'
      }

      return { ...styles, ...this.additionalContainerStyles }
    },
    /* Dynamically generate modal styles */
    modalStyles() {
      const styles = { top: `${this.scrollPosition}px` }

      if (this.isOrgMessage) {
        const maximumWidth = 500 // $maximum-width
        const viewportWidthInPx =
          window.innerWidth < maximumWidth ? window.innerWidth : maximumWidth
        const modalWidthInPx = this.width * 0.01 * viewportWidthInPx
        styles['width'] = `${modalWidthInPx}px`

        return styles
      }

      // For employees, manually calculate the vw in pixels.
      // That way, we can base it on either the fixed-width, or the viewport size - whichever is smaller
      if (this.user && this.user.isEmployee) {
        const maximumWidth = 500 // $maximum-width
        const viewportWidthInPx =
          window.innerWidth < maximumWidth ? window.innerWidth : maximumWidth
        const modalWidthInPx = this.width * 0.01 * viewportWidthInPx
        styles['width'] = `${modalWidthInPx}px`
        // All other users see a full screen
      } else {
        styles['width'] = `${this.width}vw`
      }
      return { ...styles, ...this.additionalModalStyles }
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  position: absolute;
  left: 0;
  width: 100vw;
  height: 100vh;
  min-height: 100vh !important; // in case of inheritance issues
  /* md-option seems to have a max z-index of ~15, and these need to render
   * on top of this modal in some cases. For that reason, set container z-index to 10.
   */
  z-index: 10;
  display: flex;
  flex-flow: column;
  align-items: center;
  overflow-y: scroll;
  padding: 8vh 0;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.dimmed {
  background-color: rgba($color: black, $alpha: 0.5);
}

.organization-message {
  z-index: 11;
  background: white;
  display: flex;
  flex-flow: column;
  box-sizing: border-box;
  height: auto;
  position: relative;
  border-radius: 10px;
  /* NIP 7/28/20 Ideally only the container would scroll, not the modal - but I'm having an issue
   * where the slot content overflows the modal div on mobile devices, and this is the only way
   * I've been able to resolve it. */
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.organization-message::-webkit-scrollbar {
  display: none;
}

.modal {
  border: 1px solid #eeeeee; // soft-gray
  z-index: 11;
  background: white;
  display: flex;
  flex-flow: column;
  box-sizing: border-box;
  height: auto;
  padding: 1rem;
  position: relative;
  /* NIP 7/28/20 Ideally only the container would scroll, not the modal - but I'm having an issue
   * where the slot content overflows the modal div on mobile devices, and this is the only way
   * I've been able to resolve it. */
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.box-shadow {
  box-shadow: 0 4px 16px 0 rgba($color: black, $alpha: 0.3);
}

.black_modal {
  z-index: 11;
  background: var(--dark-brand-color);
  display: flex;
  flex-flow: column;
  box-sizing: border-box;
  height: auto;
  padding: 1rem;
  position: relative;
  /* NIP 7/28/20 Ideally only the container would scroll, not the modal - but I'm having an issue
   * where the slot content overflows the modal div on mobile devices, and this is the only way
   * I've been able to resolve it. */
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;

  p {
    font-size: 15px;
    color: white;
  }

  span {
    font-size: 15px;
    color: white;
  }
}

.black_modal::-webkit-scrollbar {
  display: none;
}

.container::-webkit-scrollbar {
  display: none;
}

.modal::-webkit-scrollbar {
  display: none;
}
</style>
